import { useDialog } from "helper/customHook";
import ViewTable from "modules/DeclareLeave/components/CustomTable";
import { listFeedbackManagerDataSelector } from "modules/DeclareLeave/redux/selector";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Typography } from "@mui/material";
import pxToRem from "theme/base/pxToRem";
import {
  ApproveStatus,
  ApproveStatusValue,
  statusColor,
} from "modules/DeclareLeave/components/CustomCalendarTimeKeeping/const";

import { DIALOG } from "helper/enum/dialogEnum";
import EmptyState from "components/common/EmptyState";
import { PopupConfirm } from "./PopupConfirm";
import { setupDataRequest } from "modules/DeclareLeave/redux/handleRequest";
import { formatDateStringToDDMMYYYY } from "helper/utils";
import ResponseDetails from "modules/DeclareLeave/components/CustomCalendarTimeKeeping/components/Modals/ResponseDetail";
import { TYPE_LEAVE } from "modules/DeclareLeave/redux/getReasonLeave";
import { useNavigate, useParams } from "react-router-dom";
import pathRoutes from "routes/pathRoutes";
import { declareLeaveURL } from "modules/DeclareLeave/Pages/Main/DeclareLeaveContent/index";
import { memo } from "react";

export const ListFeedback = memo(() => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const data = useSelector(listFeedbackManagerDataSelector);

  const approveDialog = useDialog(DIALOG.APPROVE_REQUEST_POPUP);
  const viewFeedbackDetailDialog = useDialog(DIALOG.RESPONSE_DETAIL);

  const handleApprove = (e, isApprove: boolean, id: string) => {
    dispatch(setupDataRequest({ isApprove, id }));

    e.stopPropagation();
    approveDialog.open();
  };
  const columns = [
    {
      id: "createdDate",
      accessorKey: "createdDate",
      header: "Ngày tạo đơn",
      footer: (props) => props.column.id,
      cell: (info) => (
        <Typography
          sx={(theme) => ({
            fontSize: pxToRem(12),
            color: theme.palette.text.textPrimary,
            width: "100%",
            textAlign: "center",
          })}
        >
          {formatDateStringToDDMMYYYY(info.getValue())}
        </Typography>
      ),
      minSize: 120,
      pinPosition: "left",
    },
    {
      id: "employeeName",
      accessorKey: "employeeName",
      header: "Tên nhân viên",
      minSize: 115,
      pinPosition: "left",
    },
    {
      id: "positionName",
      accessorKey: "positionName",
      header: "Chức danh",
      minSize: 110,
      pinPosition: "left",
    },
    {
      id: "departmentName",
      accessorKey: "departmentName",
      header: "Đơn vị",
      footer: (props) => props.column.id,
      minSize: 110,
      pinPosition: "left",
    },

    {
      accessorKey: "date",
      header: () => "Ngày phản hồi",
      footer: (props) => props.column.id,
      cell: (info) => {
        return (
          <Typography
            sx={(theme) => ({
              fontSize: pxToRem(12),
              color: theme.palette.text.textPrimary,
              width: "100%",
              textAlign: "center",
            })}
          >
            {info.getValue() ? formatDateStringToDDMMYYYY(info.getValue()) : ""}
          </Typography>
        );
      },
      minSize: 130,
    },
    {
      accessorKey: "confirmDate",
      header: () => "Ngày duyệt",
      footer: (props) => props.column.id,
      cell: (info) => {
        return (
          <Typography
            sx={(theme) => ({
              fontSize: pxToRem(12),
              color: theme.palette.text.textPrimary,
              width: "100%",
              textAlign: "center",
            })}
          >
            {info.getValue() ? formatDateStringToDDMMYYYY(info.getValue()) : ""}
          </Typography>
        );
      },
      minSize: 110,
    },
    {
      accessorKey: "type",
      header: () => <span>Công làm việc</span>,
      footer: (props) => props.column.id,
      cell: (info) => (
        <Typography
          sx={(theme) => ({
            fontSize: pxToRem(12),
            color: theme.palette.text.textPrimary,
            width: "100%",
            textAlign: "center",
          })}
        >
          {info.getValue() === TYPE_LEAVE.FULL_DAY ? "Cả ngày" : "Nửa ngày"}
        </Typography>
      ),
      minSize: 130,
    },
    {
      accessorKey: "description",
      header: "Lý do giải trình",
      cell: (info) => {
        return (
          <Box display={"flex"} flexDirection={"column"}>
            <Typography
              sx={(theme) => ({
                fontSize: pxToRem(12),
                color: theme.palette.text.textPrimary,
              })}
            >
              {info.getValue()}
            </Typography>
          </Box>
        );
      },
      footer: (props) => props.column.id,
      minSize: 180,
    },
    {
      id: "refuseReason",
      accessorKey: "refuseReason",
      header: "Ghi chú",
      minSize: 160,
      pinPosition: "left",
    },

    {
      accessorKey: "confirmStatus",
      header: "Trạng thái",
      footer: (props) => props.column.id,
      cell: (info) => {
        const status = info.getValue();
        return (
          <Typography
            sx={(theme) => ({
              fontWeight: 600,
              fontSize: pxToRem(12),
              color: statusColor(theme, status, "color"),
            })}
          >
            {ApproveStatus[status]}
          </Typography>
        );
      },
      minSize: 100,
    },
    {
      accessorKey: "action",
      header: "",

      cell: (info) => (
        <Box
          display={"flex"}
          flexDirection={"column"}
          gap={1}
          sx={{
            background: "white",
            marginLeft: `-${pxToRem(15)}`,
            marginRight: `-${pxToRem(17)}`,
            width: `calc(100% + ${pxToRem(32)})`,
            height: `100%`,
            justifyContent: "center",
            padding: `${pxToRem(8)} ${pxToRem(16)}`,
          }}
        >
          {info.row.original.confirmStatus === ApproveStatusValue.PENDING ? (
            <>
              <Button
                onClick={(e) => handleApprove(e, true, info.row.original.id)}
                color="success"
                sx={{
                  fontSize: pxToRem(12),
                  padding: `${pxToRem(4)} ${pxToRem(10)}`,
                }}
              >
                Duyệt
              </Button>
              <Button
                sx={{
                  fontSize: pxToRem(12),
                  padding: `${pxToRem(4)} ${pxToRem(10)}`,
                }}
                onClick={(e) => handleApprove(e, false, info.row.original.id)}
                color="error"
              >
                Từ chối
              </Button>
            </>
          ) : (
            <></>
          )}
        </Box>
      ),
      minSize: 100,
    },
  ];
  const handleViewDetail = (idRecord: string) => {
    if (idRecord === id) {
      viewFeedbackDetailDialog.open();
    } else {
      navigate(
        `${pathRoutes.declareLeave.base}/${declareLeaveURL.orderManagement}/${declareLeaveURL.feedback}/${idRecord}`,
        { replace: true }
      );
    }
  };

  return (
    <>
      <ViewTable
        data={data}
        columns={columns}
        columnPinning={{
          left: [],
          right: ["action"],
        }}
        handleFetchMore={() => {}}
        messageEmpty={
          <EmptyState
            message="Chưa có đơn phản hồi nào"
            sx={(theme) => ({
              h6: {
                color: theme.palette.text.textSecondary,
              },
            })}
          />
        }
        handleOnClickedRow={(item) => handleViewDetail(item.id)}
        height={"auto"}
      />
      <ResponseDetails />
      <PopupConfirm />
    </>
  );
});
